/**
 * Bulk-import spec for the `Groups` sheet of the organizer workbook.
 *
 * EventGroups are people sets — `members.roles[]` auto-includes everyone
 * with that role on this event, `members.userIds[]` are direct adds, and
 * `members.includedGroupIds[]` is locked to empty in Phase 1 (ADR-110).
 * This importer only addresses the first two.
 *
 * The bulk callable resolves `memberEmails[]` to user uids (active
 * eventMembership) or contact ids (pre-claim staging). The admin spec
 * validates email format and role-name correctness up front; per-event
 * roster resolution happens server-side because it requires reads the
 * client doesn't have rule-level access to.
 */
import type { Role } from "../types/person";
import { invalid, pending, type ImportSpec, type ParsedRow } from "./importSpec";
import {
  ASSIGNABLE_ROLES,
  EMAIL_PATTERN,
  normalize,
} from "./importTypes";

export interface RawGroupImportRow extends Record<string, string | undefined> {
  name?: string;
  description?: string;
  memberRoles?: string;
  directMemberEmails?: string;
}

export interface ParsedGroupImportRow {
  name: string;
  description: string | null;
  /** Auto-include-by-role: maps to EventGroup.members.roles[]. */
  roles: Role[];
  /** Resolves server-side to userIds (existing users) or stagedGroupIds (contacts). */
  memberEmails: string[];
}

function splitCommaList(raw: string | undefined): string[] {
  return normalize(raw)
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

export const groupImportSpec: ImportSpec<
  RawGroupImportRow,
  ParsedGroupImportRow
> = {
  sheetName: "Groups",
  purpose:
    "Custom groups (Shuttle A, Dinner Group, VIPs, etc.). System groups (All Members, All Invitees, …) are auto-created — don't list them here.",
  columns: [
    { key: "name", note: "required — what you'd call this group out loud" },
    {
      key: "description",
      note: "optional — internal note for organizers, not shown to attendees",
    },
    {
      key: "memberRoles",
      note: "comma-separated roles to auto-include (e.g. 'invitee, companion'). Leave blank for hand-picked groups.",
    },
    {
      key: "directMemberEmails",
      note: "comma-separated emails of specific people to add by hand",
    },
  ],
  example: {
    name: "Dinner — Friday Mario's",
    description: "Private dinner for invited investors only.",
    memberRoles: "",
    directMemberEmails:
      "alice.park@example.com, eva.chen@example.com, marcus.lee@example.com",
  },
  identity: {
    idPrefix: "group",
    label: (row) => row.name,
    naturalKey: (row) => [row.name],
  },
  validate(raw, lineNumber) {
    const name = normalize(raw.name);
    const description = normalize(raw.description) || null;
    const rawRoles = splitCommaList(raw.memberRoles).map((r) =>
      r.toLowerCase(),
    );
    const memberEmails = splitCommaList(raw.directMemberEmails).map((e) =>
      e.toLowerCase(),
    );

    const base: Omit<ParsedRow<ParsedGroupImportRow>, "status" | "message"> = {
      lineNumber,
      data: {
        name,
        description,
        roles: rawRoles as Role[],
        memberEmails,
      },
    };

    if (!name) {
      return invalid(base, "Name is required");
    }

    // Role values must be in the assignable set (matches the Users importer's
    // role validation; admin/organizer/internal/invitee/companion all valid).
    for (const role of rawRoles) {
      if (!(ASSIGNABLE_ROLES as readonly string[]).includes(role)) {
        return invalid(
          base,
          `Unknown role "${role}". Must be one of: ${ASSIGNABLE_ROLES.join(", ")}`,
        );
      }
    }

    // Email format check up front. Server resolves each to uid or contact;
    // unresolvable ones come back as a row error, but we catch obvious typos
    // in the admin to surface them before the round-trip.
    for (const email of memberEmails) {
      if (!EMAIL_PATTERN.test(email)) {
        return invalid(base, `Invalid email format: "${email}"`);
      }
    }

    return pending({
      ...base,
      data: {
        ...base.data,
        roles: Array.from(new Set(rawRoles)) as Role[],
        memberEmails: Array.from(new Set(memberEmails)),
      },
    });
  },
  dedupe(rows) {
    const seen = new Set<string>();
    return rows.map((row) => {
      if (row.status !== "pending") return row;
      const key = row.data.name.toLowerCase();
      if (seen.has(key)) {
        return {
          ...row,
          status: "invalid" as const,
          message: "Duplicate group name within input",
        };
      }
      seen.add(key);
      return row;
    });
  },
};
